// ============================================================
// Projects · ProjectPage — per-project surface (real, not placeholder)
//
// Replaces the old Domains/LawPocket + Domains/PrinceHall stubs.
// Loads the project from useProjects() (the same demo + live
// hook that powers the index), renders identity + KPI strip + a
// row of "Open …" entry tiles for Content Studio, Site Builder,
// Comms, and Files. Each tile navigates into the matching surface
// scoped to this project where the surface exists, or records the
// intent + navigates to the closest available surface.
//
// IMPORTANT: this is the project SHELL surface, not the Studio /
// Site Builder UI itself. Those still need to be built per their
// existing specs (docs/WORKSPACE_SPEC.md, docs/SITE_BUILDER_SPEC.md).
// ============================================================

import { useNavigate, useParams } from 'react-router-dom'
import EmptyState from '../../components/EmptyState'
import MetricCard from '../../components/MetricCard'
import { useProject } from '../../hooks/useProjects'
import {
  SHELL_TOKENS,
  chipStyle,
  healthAccent,
  statusAccent,
  text,
  timeAgo,
} from '../../shell/styles'
import { recordShellAction } from '../../observability/sentry'

interface EntryTile {
  id: string
  label: string
  description: string
  glyph: string
  onSelect: () => void
}

export default function ProjectPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { project, counters, loading, error } = useProject(id)

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <EmptyState title="Loading project" body="Hydrating from the portfolio." />
      </div>
    )
  }

  if (!project) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div
          style={{
            ...text.kicker,
            fontSize: 10,
            letterSpacing: '0.28em',
            color: SHELL_TOKENS.inkFaint,
            marginBottom: 6,
          }}
        >
          SQUARE CODE · PROJECTS
        </div>
        <div style={text.editorial}>Project not found</div>
        <div
          style={{
            fontSize: 12,
            color: SHELL_TOKENS.inkMuted,
            marginTop: 6,
            marginBottom: 18,
            fontFamily: 'var(--font-mono)',
          }}
        >
          id: {id}
        </div>
        <EmptyState
          title="No project with that id"
          body={
            error && error !== 'project_not_found'
              ? `The Projects API returned: ${error}.`
              : 'It may have been deleted, or you may have arrived from an outdated link. Return to the index and pick a project.'
          }
        />
        <div style={{ marginTop: 14 }}>
          <button
            type="button"
            onClick={() => navigate('/square/projects')}
            style={primaryBtn}
          >
            Back to All Projects
          </button>
        </div>
      </div>
    )
  }

  const accent = statusAccent(project.status)

  const entries: EntryTile[] = [
    {
      id: 'workspace',
      label: 'Project Workspace',
      description: 'Messages · Files · Deliverables · Tasks · Calendar · Approvals',
      glyph: '◧',
      onSelect: () => {
        recordShellAction('project.open_workspace', { projectId: project.id })
        navigate(`/square/projects/${project.id}/workspace`)
      },
    },
    {
      id: 'studio',
      label: 'Content Studio',
      description: 'Documents · PDF · Slides · Sheets · Notes · Build',
      glyph: '◼',
      onSelect: () => {
        recordShellAction('project.open_studio', { projectId: project.id })
        navigate(`/square/projects/${project.id}/studio`)
      },
    },
    {
      id: 'site',
      label: 'Site Builder',
      description: 'Pages · Blocks · Assets · Preview · Publish',
      glyph: '◧',
      onSelect: () => {
        recordShellAction('project.open_site', { projectId: project.id })
        navigate(`/square/projects/${project.id}/site`)
      },
    },
    {
      id: 'hosting',
      label: 'Hosting',
      description: 'Deployments · Domains · Environments',
      glyph: '◇',
      onSelect: () => {
        recordShellAction('project.open_hosting', { projectId: project.id })
        navigate(`/square/projects/${project.id}/hosting`)
      },
    },
    {
      id: 'comms',
      label: 'Comms',
      description: 'Project communications · Threads · Messages',
      glyph: '✉',
      onSelect: () => {
        recordShellAction('project.open_comms', { projectId: project.id })
        navigate(`/square/projects/${project.id}/comms`)
      },
    },
    {
      id: 'files',
      label: 'Files',
      description: 'Deliverables · Evidence · Assets',
      glyph: '▢',
      onSelect: () => {
        recordShellAction('project.open_files', { projectId: project.id })
        navigate(`/square/command-center/workspaces/vault?project=${project.id}`)
      },
    },
  ]

  // Project Command — every project collapses into one command surface
  // (files, tasks, workflows, access, comms, team, security, portal,
  // meetings). Replaces the retired per-project "Commandery Control".
  entries.unshift({
    id: 'command',
    label: 'Project Command',
    description: 'Files · Tasks · Workflows · Access · Comms · Team · Security · Portal · Meetings',
    glyph: '◆',
    onSelect: () => {
      recordShellAction('project.open_command', { projectId: project.id })
      navigate(`/square/projects/${project.id}/command`)
    },
  })

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Identity header */}
      <div style={{ marginBottom: 22 }}>
        <button
          type="button"
          onClick={() => navigate('/square/projects')}
          style={{
            background: 'transparent',
            border: 'none',
            color: SHELL_TOKENS.inkFaint,
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            cursor: 'pointer',
            marginBottom: 12,
            padding: 0,
          }}
        >
          ← All Projects
        </button>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 16,
          }}
        >
          <div>
            <div
              style={{
                ...text.kicker,
                fontSize: 10,
                letterSpacing: '0.28em',
                color: SHELL_TOKENS.inkFaint,
                marginBottom: 6,
              }}
            >
              PROJECT
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: `${accent}14`,
                  border: `1px solid ${accent}30`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  color: accent,
                  fontFamily: 'var(--font-brand)',
                }}
              >
                ◈
              </div>
              <div style={text.editorial}>{project.name}</div>
            </div>
            <div
              style={{
                fontSize: 12,
                color: SHELL_TOKENS.inkMuted,
                marginTop: 8,
                fontFamily: 'var(--font-mono)',
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}
            >
              {project.status && <span style={chipStyle(accent)}>{project.status}</span>}
              {project.domain && <span style={chipStyle(SHELL_TOKENS.inkFaint)}>{project.domain}</span>}
              <span>updated {timeAgo(project.updated_at)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
          marginBottom: 26,
        }}
      >
        <MetricCard
          label="Health"
          value={project.health_score == null ? '—' : `${project.health_score}%`}
          sub={
            project.health_score == null
              ? 'not scored'
              : project.health_score > 70
              ? 'on track'
              : 'needs attention'
          }
          tone={project.health_score == null ? 'default' : project.health_score > 70 ? 'default' : 'warning'}
        />
        <MetricCard
          label="Open requests"
          value={counters?.open_requests ?? '—'}
          sub={counters?.open_requests == null ? 'not linked' : 'awaiting action'}
          tone={(counters?.open_requests ?? 0) > 0 ? 'warning' : 'default'}
        />
        <MetricCard
          label="Active files"
          value={counters?.active_files ?? '—'}
          sub={counters?.active_files == null ? 'unavailable' : 'in flight'}
        />
        <MetricCard
          label="Latest run"
          value={project.latest_run_status || '—'}
          sub={counters?.last_run_at ? `updated ${timeAgo(counters.last_run_at)}` : (project.client_name || project.name)}
        />
      </section>

      {/* Entry tiles — Studio / Site / Comms / Files */}
      <section>
        <div
          style={{
            ...text.kicker,
            fontSize: 9,
            letterSpacing: '0.24em',
            color: SHELL_TOKENS.inkWhisper,
            marginBottom: 10,
          }}
        >
          OPEN
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 10,
          }}
        >
          {entries.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={e.onSelect}
              onMouseEnter={(ev) => {
                const t = ev.currentTarget
                t.style.borderColor = SHELL_TOKENS.brandLine
                t.style.background = `linear-gradient(135deg, ${SHELL_TOKENS.brandSoft}, ${SHELL_TOKENS.surface})`
                t.style.transform = 'translateY(-2px)'
                t.style.boxShadow = `0 4px 20px ${SHELL_TOKENS.brandGlow}`
              }}
              onMouseLeave={(ev) => {
                const t = ev.currentTarget
                t.style.borderColor = SHELL_TOKENS.border
                t.style.background = SHELL_TOKENS.surface
                t.style.transform = 'translateY(0)'
                t.style.boxShadow = 'none'
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '20px 20px',
                borderRadius: 14,
                border: `1px solid ${SHELL_TOKENS.border}`,
                background: SHELL_TOKENS.surface,
                color: SHELL_TOKENS.ink,
                cursor: 'pointer',
                fontFamily: 'inherit',
                textAlign: 'left',
                transition: 'all 0.2s ease',
              }}
            >
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 12,
                  background: `linear-gradient(135deg, ${SHELL_TOKENS.brandSoft}, rgba(139,92,246,0.15))`,
                  border: `1px solid ${SHELL_TOKENS.brandLine}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  color: SHELL_TOKENS.brand,
                  fontFamily: 'var(--font-brand)',
                  flexShrink: 0,
                  boxShadow: `0 0 12px ${SHELL_TOKENS.brandGlow}`,
                }}
              >
                {e.glyph}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: SHELL_TOKENS.ink,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {e.label}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: SHELL_TOKENS.inkMuted,
                    marginTop: 4,
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {e.description}
                </div>
              </div>
              <span
                style={{
                  fontSize: 18,
                  color: SHELL_TOKENS.brand,
                  flexShrink: 0,
                  fontWeight: 700,
                  opacity: 0.6,
                }}
              >
                →
              </span>
            </button>
          ))}
        </div>

        {/* Health bar */}
        <div
          style={{
            marginTop: 14,
            height: 4,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.04)',
          }}
        >
          <div
            style={{
              height: '100%',
              borderRadius: 2,
              width: `${Math.max(0, Math.min(100, project.health_score ?? 0))}%`,
              background: healthAccent(project.health_score),
              transition: 'width 1s',
            }}
          />
        </div>
      </section>

      {/* Repository context */}
      <section style={{ marginTop: 22 }}>
        <div
          style={{
            ...text.kicker,
            fontSize: 9,
            letterSpacing: '0.24em',
            color: SHELL_TOKENS.inkWhisper,
            marginBottom: 10,
          }}
        >
          REPOSITORY
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          <RepoCard
            provider="GitHub"
            url={project.github_repo_url}
            glyph="◉"
            hint="Code spine · Codespaces · CI/CD"
          />
          <RepoCard
            provider="GitLab"
            url={project.gitlab_url}
            glyph="◎"
            hint="Client collaboration · delivery logs"
          />
        </div>
      </section>
    </div>
  )
}

function RepoCard({ provider, url, glyph, hint }: {
  provider: string
  url?: string | null
  glyph: string
  hint: string
}) {
  const linked = !!url

  return (
    <div
      style={{
        padding: '14px 16px',
        borderRadius: 12,
        background: SHELL_TOKENS.surface,
        border: `1px solid ${linked ? SHELL_TOKENS.brandLine : SHELL_TOKENS.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: linked ? SHELL_TOKENS.brandSoft : 'rgba(255,255,255,0.02)',
          border: `1px solid ${linked ? SHELL_TOKENS.brandLine : SHELL_TOKENS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          color: linked ? SHELL_TOKENS.brand : SHELL_TOKENS.inkFaint,
          fontFamily: 'var(--font-brand)',
          flexShrink: 0,
        }}
      >
        {glyph}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: SHELL_TOKENS.ink }}>
          {provider}
        </div>
        {linked ? (
          <a
            href={url!}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 10,
              color: SHELL_TOKENS.brand,
              fontFamily: 'var(--font-mono)',
              textDecoration: 'none',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginTop: 2,
            }}
          >
            {url!.replace(/^https?:\/\//, '')}
          </a>
        ) : (
          <div style={{ fontSize: 10, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)', marginTop: 2 }}>
            Not linked · {hint}
          </div>
        )}
      </div>
      {linked && (
        <span style={{ fontSize: 10, color: SHELL_TOKENS.green, fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0 }}>
          Linked
        </span>
      )}
    </div>
  )
}

const primaryBtn: React.CSSProperties = {
  padding: '9px 16px',
  borderRadius: 8,
  border: `1px solid ${SHELL_TOKENS.brandLine}`,
  background: SHELL_TOKENS.brandSoft,
  color: SHELL_TOKENS.brand,
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  fontFamily: 'var(--font-mono)',
}
